/**
 * Auto-preenchimento dos exames modulares a partir do texto clínico.
 *
 * Caminho INVERSO da extração principal: enquanto `buildExtractionContext`
 * manda os exames preenchidos PRA a IA (gerar evolução/laudos), aqui a IA lê
 * o texto já transcrito/digitado (transcrição do áudio + observações) e
 * DEVOLVE os valores objetivos pra preencher os campos OD/OE/único dos
 * módulos (PIO, Refração, AV, Biometria…).
 *
 * Três funções PURAS (sem rede, sem React, testáveis isoladamente):
 *   1. `buildExamFillPrompt`  — monta o prompt enviado à IA (action 'general').
 *   2. `parseExamFillResponse` — extrai/valida o JSON da resposta contra o catálogo.
 *   3. `applyExamFill`         — mescla os valores no estado atual (não-destrutivo).
 *
 * A chamada de rede (GeminiClient.analyzeText) fica no componente; este módulo
 * só transforma texto ↔ dados.
 */

import type {
  EncounterExamsState,
  ExamFieldDef,
  ExamModuleDef,
  ExamModuleEntry,
  ExamOrigin,
} from '@/types/encounter';

/**
 * Valores extraídos pela IA, já validados: módulo → campo → célula.
 * Só contém módulos/campos conhecidos do catálogo e células não-vazias.
 */
export type ExamFillMap = Record<
  string,
  Record<string, { OD?: string; OE?: string; mono?: string }>
>;

// ─── 1. Prompt ───

/**
 * Monta o prompt de extração estruturada dos exames. Descreve cada módulo do
 * catálogo (id, label, campos com chave/layout/opções) e pede um JSON enxuto
 * só com o que estiver EXPLÍCITO no texto. Função pura.
 */
export function buildExamFillPrompt(catalog: ExamModuleDef[], sourceText: string): string {
  const catalogLines = catalog
    .map((mod) => {
      const fieldLines = mod.fields
        .map((f) => {
          const layout = f.layout ?? 'eyes';
          const keyHint =
            layout === 'mono'
              ? 'valor único → chave "mono"'
              : 'por olho → chaves "OD" e/ou "OE"';
          const opts =
            f.kind === 'select' && f.options?.length
              ? ` | opções válidas (escolha UMA exata): ${f.options.join(' | ')}`
              : '';
          const suffix = f.suffix ? ` em ${f.suffix}` : '';
          return `    - "${f.key}" (${f.label}${suffix}) [${keyHint}]${opts}`;
        })
        .join('\n');
      const hint = mod.hint ? ` — ${mod.hint}` : '';
      return `• "${mod.id}" — ${mod.label}${hint}:\n${fieldLines}`;
    })
    .join('\n');

  return `Você é um assistente oftalmológico. A partir do TEXTO CLÍNICO abaixo (transcrição da consulta e observações do médico), extraia SOMENTE os valores de exames objetivos que estiverem EXPLÍCITOS no texto e devolva-os para preencher os módulos estruturados do atendimento.

### TEXTO CLÍNICO
${sourceText.trim()}

### MÓDULOS DISPONÍVEIS (use a CHAVE entre aspas exatamente como aparece)
${catalogLines}

### FORMATO DA RESPOSTA
Responda EXCLUSIVAMENTE com um objeto JSON válido (sem markdown, sem comentários, sem nenhum texto fora do JSON), no formato:
{
  "<idDoModulo>": { "<chaveDoCampo>": { "OD": "<valor>", "OE": "<valor>" } },
  "<idDoModulo>": { "<chaveDoCampo>": { "mono": "<valor>" } }
}

### REGRAS (siga à risca)
1. Inclua APENAS módulos e campos cujo valor esteja CLARAMENTE dito no texto. Se não foi dito, OMITA a chave — nunca invente, estime nem deduza.
2. Use as CHAVES exatas dos módulos e campos listados acima. Não crie chaves novas.
3. Campos "por olho": preencha "OD" e/ou "OE" (apenas o lado mencionado). Campos de "valor único": use "mono".
4. Para campos com opções válidas, devolva EXATAMENTE uma das opções; se o texto não casar com nenhuma, omita o campo.
5. Devolva os números como no texto (ex.: PIO "14"/"16"; refração "-2,00" ou "-2.00"; AV "20/20"; eixo "180"). NÃO acrescente unidades nem símbolos.
6. Se o texto não trouxer nenhum valor de exame, devolva um objeto vazio: {}.`;
}

// ─── 2. Parsing ───

/**
 * Extrai o objeto JSON de uma resposta que pode vir cercada por ```json … ```
 * ou com prosa antes/depois. Espelha o `extractJsonObject` do backend pra que
 * a action 'general' (texto cru) seja parseável no front. Função pura.
 */
function extractJsonObject(raw: string): string | null {
  if (!raw) return null;
  let s = raw.trim();
  const fence = s.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i);
  if (fence) s = fence[1].trim();
  const first = s.indexOf('{');
  const last = s.lastIndexOf('}');
  if (first !== -1 && last !== -1 && last > first) return s.slice(first, last + 1);
  return null;
}

/** Lê um valor escalar (string/number) como string aparada, ou undefined. */
function readStr(v: unknown): string | undefined {
  if (typeof v === 'string') {
    const t = v.trim();
    return t || undefined;
  }
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return undefined;
}

/** Normaliza pra casar opções de `select`: minúsculas, sem acento, aparado. */
function normalizeForMatch(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim();
}

/**
 * Valida o valor contra as opções de um campo `select`. Para campos livres,
 * devolve o próprio valor. Para `select`, devolve a opção canônica (casando
 * sem acento/caixa) ou undefined se nada casar.
 */
function validateOption(def: ExamFieldDef, val: string | undefined): string | undefined {
  if (!val) return undefined;
  if (def.kind === 'select' && def.options && def.options.length) {
    const target = normalizeForMatch(val);
    return def.options.find((o) => normalizeForMatch(o) === target);
  }
  return val;
}

/**
 * Extrai e valida o JSON da IA contra o catálogo. Descarta módulos/campos
 * desconhecidos, valida opções de `select` e respeita o layout de cada campo
 * (eyes → OD/OE; mono → mono). Tolerante: aceita célula como string solta num
 * campo `mono`, e chaves "od"/"oe" minúsculas. Nunca lança — em qualquer
 * problema de parse devolve {}. Função pura.
 */
export function parseExamFillResponse(rawText: string, catalog: ExamModuleDef[]): ExamFillMap {
  const jsonStr = extractJsonObject(rawText);
  if (!jsonStr) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(jsonStr);
  } catch {
    return {};
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

  const byId = new Map(catalog.map((m) => [m.id, m]));
  const out: ExamFillMap = {};

  for (const [moduleId, rawFields] of Object.entries(parsed as Record<string, unknown>)) {
    const mod = byId.get(moduleId);
    if (!mod || !rawFields || typeof rawFields !== 'object' || Array.isArray(rawFields)) continue;

    const fieldDefs = new Map(mod.fields.map((f) => [f.key, f]));
    const cleanFields: Record<string, { OD?: string; OE?: string; mono?: string }> = {};

    for (const [fieldKey, rawVal] of Object.entries(rawFields as Record<string, unknown>)) {
      const def = fieldDefs.get(fieldKey);
      if (!def) continue;
      const layout = def.layout ?? 'eyes';
      const cell: { OD?: string; OE?: string; mono?: string } = {};

      if (layout === 'mono') {
        let val: string | undefined;
        if (rawVal && typeof rawVal === 'object' && !Array.isArray(rawVal)) {
          const o = rawVal as Record<string, unknown>;
          val = readStr(o.mono) ?? readStr(o.OD) ?? readStr(o.od) ?? readStr(o.OE) ?? readStr(o.oe);
        } else {
          val = readStr(rawVal);
        }
        const ok = validateOption(def, val);
        if (ok) cell.mono = ok;
      } else if (rawVal && typeof rawVal === 'object' && !Array.isArray(rawVal)) {
        const o = rawVal as Record<string, unknown>;
        const od = validateOption(def, readStr(o.OD) ?? readStr(o.od));
        const oe = validateOption(def, readStr(o.OE) ?? readStr(o.oe));
        if (od) cell.OD = od;
        if (oe) cell.OE = oe;
      }
      // Campo 'eyes' com valor escalar é descartado de propósito: não dá pra
      // saber o olho com segurança, e chutar OD/OE arriscaria dado errado.

      if (cell.OD || cell.OE || cell.mono) cleanFields[fieldKey] = cell;
    }

    if (Object.keys(cleanFields).length > 0) out[moduleId] = cleanFields;
  }

  return out;
}

// ─── 3. Merge ───

export interface ApplyExamFillResult {
  /** Próximo estado dos exames, pronto pra `setEncounterExams`. */
  next: EncounterExamsState;
  /** Quantas células (OD/OE/mono) foram efetivamente preenchidas. */
  filledCount: number;
  /** Ids dos módulos criados automaticamente (não existiam antes). */
  addedModuleIds: string[];
}

export interface ApplyExamFillOptions {
  /** Cria módulos detectados que ainda não estavam na lista. Default: true. */
  autoAdd?: boolean;
  /** Sobrescreve células já preenchidas pelo médico. Default: false. */
  overwrite?: boolean;
}

/**
 * Mescla os valores extraídos no estado atual dos exames.
 *
 * Comportamento padrão (escolhido pelo médico):
 *   - autoAdd=true  → módulos detectados que não estão na lista são adicionados.
 *   - overwrite=false → nunca apaga/troca o que o médico já digitou; só
 *     preenche células vazias.
 *
 * Função pura: não muta `current`, devolve um novo estado.
 */
export function applyExamFill(
  current: EncounterExamsState,
  fill: ExamFillMap,
  catalog: ExamModuleDef[],
  options?: ApplyExamFillOptions,
): ApplyExamFillResult {
  const autoAdd = options?.autoAdd ?? true;
  const overwrite = options?.overwrite ?? false;
  const known = new Set(catalog.map((m) => m.id));

  // Clona as entradas (incluindo o nível das células) pra não mutar o estado.
  const entries: ExamModuleEntry[] = current.entries.map((e) => ({
    moduleId: e.moduleId,
    values: Object.fromEntries(Object.entries(e.values).map(([k, v]) => [k, { ...v }])),
  }));
  const indexById = new Map(entries.map((e, i) => [e.moduleId, i]));

  // Clona o mapa de origem (células preenchidas pela IA) e o estende com as
  // células que ESTE merge preencher — pra o painel sinalizar visualmente.
  const aiFilled: Record<string, Record<string, ExamOrigin>> = {};
  for (const [mid, fields] of Object.entries(current.aiFilled ?? {})) {
    aiFilled[mid] = {};
    for (const [fk, cell] of Object.entries(fields)) aiFilled[mid][fk] = { ...cell };
  }
  const markAi = (moduleId: string, fieldKey: string, eye: 'OD' | 'OE' | 'mono') => {
    const mod = (aiFilled[moduleId] ??= {});
    const field = (mod[fieldKey] ??= {});
    field[eye] = true;
  };

  let filledCount = 0;
  const addedModuleIds: string[] = [];

  /** Grava a célula (se vazia/overwrite). Retorna true se efetivamente escreveu. */
  const setCell = (
    values: ExamModuleEntry['values'],
    fieldKey: string,
    eye: 'OD' | 'OE' | 'mono',
    val: string,
  ): boolean => {
    const prev = values[fieldKey] ?? {};
    const existing = prev[eye]?.trim();
    if (existing && !overwrite) return false; // não-destrutivo: mantém o do médico
    if (existing === val) return false; // nada a fazer
    values[fieldKey] = { ...prev, [eye]: val };
    filledCount++;
    return true;
  };

  for (const [moduleId, fields] of Object.entries(fill)) {
    if (!known.has(moduleId)) continue;
    let idx = indexById.get(moduleId);
    if (idx == null) {
      if (!autoAdd) continue;
      idx = entries.length;
      entries.push({ moduleId, values: {} });
      indexById.set(moduleId, idx);
      addedModuleIds.push(moduleId);
    }
    const entry = entries[idx];
    for (const [fieldKey, cell] of Object.entries(fields)) {
      if (cell.OD && setCell(entry.values, fieldKey, 'OD', cell.OD)) markAi(moduleId, fieldKey, 'OD');
      if (cell.OE && setCell(entry.values, fieldKey, 'OE', cell.OE)) markAi(moduleId, fieldKey, 'OE');
      if (cell.mono && setCell(entry.values, fieldKey, 'mono', cell.mono)) markAi(moduleId, fieldKey, 'mono');
    }
  }

  // Remove módulos recém-adicionados que acabaram sem nenhum valor (ex.: tudo
  // já existia idêntico). Mantém a lista limpa.
  const hasValue = (e: ExamModuleEntry) =>
    Object.values(e.values).some((v) => v.OD?.trim() || v.OE?.trim() || v.mono?.trim());
  const finalEntries = entries.filter(
    (e) => !addedModuleIds.includes(e.moduleId) || hasValue(e),
  );
  const keptAdded = addedModuleIds.filter((id) => finalEntries.some((e) => e.moduleId === id));

  // Descarta origem de módulos que sumiram (added vazio removido).
  const finalModuleIds = new Set(finalEntries.map((e) => e.moduleId));
  for (const mid of Object.keys(aiFilled)) {
    if (!finalModuleIds.has(mid)) delete aiFilled[mid];
  }

  return {
    next: { ...current, entries: finalEntries, aiFilled },
    filledCount,
    addedModuleIds: keptAdded,
  };
}
